import { computed, watch } from 'vue';
import { defineStore } from 'pinia';
import { useHipSpineStore } from '@/views/3d-planner/hipSpine/hipSpineStore';
import { useHipSpineSyncStore } from '@/views/3d-planner/hipSpine/hipSpineSyncStore';
import { useHipSpinePostureSavePlan } from '@/views/3d-planner/hipSpine/useHipSpinePostureSavePlan';
import { useHipSpineXRayEditViewModel } from '@/views/3d-planner/hipSpine/xrayEdit/useHipSpineXRayEditViewModel';
import { POSTURE_TABS, createModalState } from '@/views/3d-planner/hipSpine/xrayModal/modalState';
import { createModalResetGating, createModalApproveGating } from '@/views/3d-planner/hipSpine/xrayModal/modalGating';
import { createModalActions } from '@/views/3d-planner/hipSpine/xrayModal/modalActions';

export const useHipSpineXRayModalViewModel = defineStore('hip-spine-xray-modal', () => {
    const ctx = createModalState(
        useHipSpineStore(),
        useHipSpineSyncStore(),
        useHipSpinePostureSavePlan(),
        useHipSpineXRayEditViewModel(),
    );
    const { store, sync, isResetting, resetError, isApproving, approveError } = ctx;

    const activePostureId = computed({
        get: () => store.activePostureId,
        set: (value) => store.setActivePostureId(value),
    });

    const activePanelView = computed({
        get: () => store.activePanelView,
        set: (value) => store.setActivePanelView(value),
    });

    // Requirement #5: switching posture always returns to the Annotations tab.
    watch(
        () => store.activePostureId,
        () => store.setActivePanelView('annotations'),
    );

    return {
        postureTabs: POSTURE_TABS,
        activePostureId,
        activePanelView,
        isApproving,
        approveError,
        isResetting,
        resetError,
        ...createModalResetGating(ctx),
        ...createModalApproveGating(ctx),
        ...createModalActions(ctx),
        pendingPostureCount: computed(() => store.pendingPostureCount),
        totalPostureCount: computed(() => store.totalPostureCount),
        isPosturePending: store.isPosturePending,
        isSaving: computed(() => sync.isSaving),
        // A reset persists server truth (POST reset + landmark re-fetch), so it surfaces on
        // the same save indicator as an auto-save: 'saving' while in flight, then the sync
        // store's idle state lets the indicator flip to its "Saved" confirmation. A reset
        // failure is shown by the dedicated `resetError` alert, so we fall through to the
        // sync status rather than forcing a misleading "Save failed / retrying" here.
        saveStatus: computed(() => (isResetting.value ? 'saving' : sync.status)),
    };
});
